import {
  TButton,
  TCheckbox,
  TDatepicker,
  TInput,
  TModal,
  TPagination,
  TRadio,
  TRichSelect,
  TSelect,
  TTextarea,
  TToggle,
} from 'vue-tailwind/dist/components';

export const settings = {
  // Use the following syntax
  // {component-name}: {
  //   component: {importedComponentObject},
  //   props: {
  //     {propToOverride}: {newDefaultValue}
  //     {propToOverride2}: {newDefaultValue2}
  //   }
  // }
  't-input': {
    component: TInput,
    props: {
      fixedClasses: `w-full px-2
          border border-solid
          focus:outline-none focus:shadow focus:shadow-purple-4
          `,
      classes: 'border-purple-200 focus:border-purple-400',
      variants: {
        error: 'rounded border-red-700 focus:border-red-700',
        disabled: 'rounded bg-gray-50 border-gray-300 text-gray-500 cursor-not-allowed',
      },
    },
  },
  // Inline input
  't-input-inline': {
    component: TInput,
    props: {
      fixedClasses: `w-full px-3 border border-solid
        focus:outline-none focus:shadow focus:shadow-purple-4`,
      classes: 'rounded-none border-purple-200 focus:border-r focus:border-purple-400',
      variants: {
        error: 'border-red-700 focus:border-red-700',
        disabled: 'rounded bg-gray-50 border-gray-300 text-gray-500 cursor-not-allowed',
      },
    },
  },
  // Inline input
  't-input-table': {
    component: TInput,
    props: {
      fixedClasses: `w-full h-10 px-1 border border-solid
        focus:outline-none focus:shadow focus:shadow-purple-4`,
      classes:
        'rounded-none border-r-0 border-b-0 border-purple-200 focus:border-r focus:border-b focus:border-purple-400',
      variants: {
        error: 'border-red-700 focus:border-red-700',
        disabled: 'bg-gray-50 border-gray-300 text-gray-500 cursor-not-allowed',
        last: 'border-purple-200 border-r-1 border-b-0 focus:border-r focus:border-b focus:border-purple-400',
      },
    },
  },
  't-input-label': {
    component: TInput,
    props: {
      fixedClasses: `w-full px-2
          border border-solid
          focus:outline-none focus:shadow focus:shadow-purple-4
          `,
      classes: 'border-purple-200 focus:border-purple-400 cursor-text disabled:cursor-not-allowed',
      variants: {
        error: 'border-red-700 focus:border-red-700',
        disabled: 'bg-gray-50 border-gray-300 text-gray-500 cursor-not-allowed',
        table: 'border-r-0 border-b-0 border-purple-200 focus:border-r focus:border-b focus:border-purple-400',
        'table-last': 'border-b-0 border-purple-200 focus:border-b focus:border-purple-400',
        rounded: 'rounded border-purple-200 focus:border-purple-400',
      },
    },
  },
  //
  't-checkbox': {
    component: TCheckbox,
    props: {
      fixedClasses: `border border-solid rounded
          focus:outline-none focus:shadow focus:shadow-purple-4
          `,
      classes: 'border-purple-200 focus:border-purple-400',
      variants: {
        error: 'border-red-700 focus:border-red-700',
        disabled: 'bg-gray-50 border-gray-300 text-gray-500 cursor-not-allowed',
      },
    },
  },
  //
  't-button': {
    component: TButton,
    props: {
      fixedClasses: `block px-4 py-8 transition duration-100 ease-in-out 
          focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none focus:ring-opacity-50 
          disabled:opacity-50 disabled:cursor-not-allowed`,
      classes: 'text-white bg-blue-500 border border-transparent shadow-sm rounded hover:bg-blue-600',
      variants: {
        secondary: 'text-gray-800 bg-white border border-gray-300 shadow-sm hover:text-gray-600',
        error: 'text-white bg-red-500 border border-transparent rounded shadow-sm hover:bg-red-600',
        success: 'text-white border border-transparent rounded shadow-sm hover:bg-green-600',
        link: 'text-blue-500 underline hover:text-blue-600',
      },
    },
  },
  't-pagination': {
    component: TPagination,
    props: {
      classes: {
        wrapper: 'bg-gray mx-auto text-center flex space-x-2',
        element: 'w-9 h-9 border border-gray-200 rounded',
        activeElement: 'w-9 h-9 rounded',
        disabledElement: 'w-9 h-9 border border-gray-200 rounded',
        ellipsisElement: 'w-9 h-9 hidden md:inline',
        activeButton: `border border-purple-400 bg-purple-400 w-full h-full rounded text-white 
            hover:bg-purple-600 transition duration-100 ease-in-out focus:ring-2 focus:ring-purple-500 
            focus:outline-none focus:ring-opacity-50`,
        disabledButton:
          'border border-gray-200 opacity-50 w-full h-full cursor-not-allowed transition duration-100 ease-in-out',
        button: `border border-gray-200 hover:bg-purple-100 hover:border-purple-100 w-full h-full 
            active:ring-2 hover:ring-2 transition duration-100 ease-in-out focus:ring-2 focus:ring-purple-500 
            focus:outline-none focus:ring-opacity-50`,
        ellipsis: '',
      },
    },
  },
  't-select': {
    component: TSelect,
    props: {
      wrapped: true,
      classes: {
        wrapper: 'relative w-20 border border-purple-300 rounded',
        input: `block w-full py-2 pl-3 pr-10 text-black placeholder-gray-400 
        transition duration-100 ease-in-out bg-white shadow-sm bg-none rounded
        focus:ring-2 focus:ring-purple-500 focus:outline-none focus:ring-opacity-50 focus:border-purple-500
        disabled:opacity-50 disabled:cursor-not-allowed `,
        arrowWrapper: 'pointer-events-none absolute inset-y-0 right-0 flex items-center px-2',
        arrow: 'fill-current h-4 w-4',
      },
      variants: {
        // wrapped: 'w-12 h-12',
      },
    },
  },
  //
  't-select-table': {
    component: TSelect,
    props: {
      wrapped: true,
      classes: {
        wrapper: 'relative border border-purple-200 border-r-0 border-b-0',
        input: `block w-full h-full py-2 pl-3 pr-10 text-black placeholder-gray-400 
        transition duration-100 ease-in-out bg-white shadow-sm bg-none
        focus:ring-1 focus:ring-purple-400 focus:outline-none focus:ring-opacity-50 focus:border-purple-400
        focus:border-r focus:border-b focus:border-purple-400
        disabled:opacity-50 disabled:cursor-not-allowed `,
        arrowWrapper: 'pointer-events-none absolute inset-y-0 right-0 flex items-center px-0',
        arrow: 'fill-current h-4 w-4',
      },
      variants: {
        // wrapped: 'w-12 h-12',
      },
    },
  },
  //
  't-rich-select': {
    component: TRichSelect,
    props: {
      fixedClasses: {
        wrapper: 'relative h-full',
        searchBox: `w-full h-10 mr-3 px-3 outline-none 
          border border-solid border-purple-200 rounded`,
        selectButton: `w-full h-full flex text-left justify-between items-center px-3 py-2 text-black
          transition duration-100 ease-in-out border rounded shadow-sm 
          focus:border-purple-400 focus:outline-none focus:ring-opacity-50
          disabled:opacity-50 disabled:cursor-not-allowed`,
        buttonWrapper: `inline-block relative w-full h-12
          bg-white border border-solid rounded 
          focus:outline-none focus:shadow-down`,
        selectButtonLabel: 'block truncate',
        selectButtonPlaceholder: 'block truncate text-gray-400',
        selectButtonIcon: 'fill-current flex-shrink-0 ml-1 h-4 w-4 text-gray-600',
        selectButtonClearButton: `rounded flex flex-shrink-0 items-center justify-center absolute
          right-0 top-0 m-2 h-6 w-6 transition duration-100 ease-in-out hover:bg-blue-100 text-gray-600`,
        selectButtonClearIcon: 'fill-current h-3 w-3 mt-1',
        dropdown:
          'absolute w-full z-10 -mt-1 border-b border-l border-r rounded-b bg-white border-purple-200 shadow-down',
        dropdownFeedback: 'pb-2 text-gray-400 text-sm',
        loadingMoreResults: 'pb-2 px-3 text-gray-400 text-sm',
        optionsList: 'ma-0 pa-0 pl-2 overflow-auto',
        searchWrapper: 'inline-block w-full p-2 placeholder-gray-400',
        optgroup: 'ma-0 pa-0 text-gray-400 uppercase text-xs py-1',
        disabledOption: 'opacity-50 cursor-not-allowed',
        highlightedOption: 'bg-purple-100 cursor-pointer',
        selectedOption: 'bg-purple-50 cursor-pointer',
        selectedHighlightedOption: 'bg-purple-200 cursor-pointer',
        optionContent: 'ma-0 pa-0 ma-0 pa-0 flex justify-between items-center py-2 pl-2',
        optionLabel: 'truncate block ma-0 pa-0',
        selectedIcon: 'fill-current h-4 w-4',
      },
      classes: {
        buttonWrapper: 'border-purple-200 focus:border-purple-400',
      },
      variants: {
        error: {
          buttonWrapper: 'border-red-700 focus:border-red-700',
        },
        disabled: {
          selectButton: 'cursor-not-allowed',
          buttonWrapper: 'bg-gray-50 border-gray-300 text-gray-500 cursor-not-allowed',
        },
      },
    },
  },
  't-radio': {
    component: TRadio,
    props: {
      fixedClasses:
        'border border-solid border-purple-400 w-4 h-4 rounded-full appearance-none outline-none cursor-pointer',
      classes: 'border-purple-400',
      variants: {
        error: 'text-red-500 border-red-300',
        success: 'text-green-500 border-green-300',
      },
    },
  },
  //
  't-textarea': {
    component: TTextarea,
    props: {
      fixedClasses: `w-full px-2 
        border border-solid rounded outline-none resize-none`,
      classes: `flex flex-column h-24 border-purple-200 leading-4
                focus:border-purple-300 
                active:border-purple-400`,
      variants: {
        error: 'border-red-700 focus:border-red-700',
        danger: 'border-red-300 bg-red-50 placeholder-red-200 text-red-900',
        success: 'border-green-300 bg-green-50 placeholder-gray-400 text-green-900',
        notrounded: 'border-purple-200 rounded-none',
        disabled: 'bg-gray-50 border-gray-300 text-gray-500 cursor-not-allowed',
      },
    },
  },
  't-modal': {
    component: TModal,
    props: {
      fixedClasses: {
        overlay:
          'z-40 overflow-auto scrolling-touch left-0 top-0 bottom-0 right-0 w-full h-full fixed bg-black bg-opacity-30',
        wrapper: 'min-w-80 relative z-50 flex align-center justify-center py-10 px-5',
        modal: 'overflow-visible relative rounded border border-purple-200',
        body: '',
        header: 'border-b border-purple-200 p-3 rounded-t bg-purple-50 text-xl leading-6',
        footer: 'p-3 rounded-b bg-white',
        close: `absolute top-2 right-2 flex items-center justify-center h-8 w-8 
           transition duration-100 ease-in-out 
           focus:ring-2 focus:ring-blue-500 focus:outline-none focus:ring-opacity-50`,
      },
      classes: {
        overlay: '',
        wrapper: '',
        modal: 'bg-white rounded',
        body: '',
        // body: 'p-3',
        header: '',
        footer: '',
        close: '',
        closeIcon: 'fill-current h-6 w-6',
        overlayEnterClass: 'opacity-0',
        overlayEnterActiveClass: 'transition ease-out duration-100',
        overlayEnterToClass: 'opacity-100',
        overlayLeaveClass: 'opacity-100',
        overlayLeaveActiveClass: 'transition ease-in duration-75',
        overlayLeaveToClass: 'opacity-0',
        enterClass: '',
        enterActiveClass: '',
        enterToClass: '',
        leaveClass: '',
        leaveActiveClass: '',
        leaveToClass: '',
      },
      variants: {
        danger: {
          overlay: 'bg-red-100',
          header: 'border-red-50 text-red-700',
          close: 'bg-red-50 text-red-700 hover:bg-red-200 border-red-100 border',
          modal: 'bg-white border border-red-100 shadow-lg',
          footer: 'bg-red-50',
        },
      },
    },
  },
  't-toggle': {
    component: TToggle,
    props: {
      fixedClasses: {
        wrapper: 'relative inline-flex flex-shrink-0 cursor-pointer transition-colors ease-in-out duration-200',
        wrapperChecked: `relative inline-flex p-0.5 flex-shrink-0 cursor-pointer 
                         transition-colors ease-in-out duration-200  
                         border-2 border-transparent focus:outline-none`,
        button: 'inline-block absolute transform translate-x-0 transition ease-in-out duration-200',
        buttonChecked: 'inline-block absolute transform translate-x-full transition ease-in-out duration-200',
        checkedPlaceholder: 'inline-block',
        uncheckedPlaceholder: 'inline-block',
      },
      classes: {
        wrapper: 'w-full bg-white p-0.5 focus:outline-none rounded-sm border border-purple-400 rounded outline-none',
        wrapperChecked:
          'w-full bg-white p-0.5 focus:outline-none rounded-sm border border-purple-400 rounded outline-none',
        wrapperDisabled: `inline-flex flex-shrink-0 p-0.5 mt-0.5 border border-solid border-purple-400 rounded 
                          cursor-pointer transition-colors ease-in-out duration-200 
                          opacity-50 cursor-not-allowed`,
        wrapperCheckedDisabled: `inline-flex flex-shrink-0 p-0.5 mt-0.5 border border-solid border-purple-400 rounded 
                          cursor-pointer transition-colors ease-in-out duration-200 
                          opacity-50 cursor-not-allowed`,
        button: `rounded-sm w-1/2 h-6 bg-purple-400 shadow flex items-center justify-center text-white text-sm 
                 focus:outline-none outline-none select-none`,
        buttonChecked: `rounded-sm w-1/2 h-6 bg-purple-400 text-white shadow flex items-center justify-center 
                        text-sm focus:outline-none outline-none select-none`,
        checkedPlaceholder: `rounded-sm w-1/2 h-6 flex items-center justify-center text-purple-400 text-sm 
                             outline-none select-none`,
        uncheckedPlaceholder:
          'rounded-sm w-1/2 h-6 flex items-center justify-center text-purple-400 text-sm outline-none select-none',
      },
      variants: {
        danger: {
          wrapperChecked: 'bg-red-500 rounded-full',
          wrapperCheckedDisabled: 'bg-red-500 rounded-full',
        },
        success: {
          wrapperChecked: 'bg-green-500 rounded-full',
          wrapperCheckedDisabled: 'bg-green-500 rounded-full',
        },
        box: {
          wrapper: 'bg-gray-100 rounded-sm border-2 border-transparent focus:outline-none',
          wrapperChecked: 'bg-purple-400 rounded-sm',
          wrapperCheckedDisabled: 'bg-purple-400 rounded-sm',
          button: 'h-6 w-6 rounded-sm bg-white shadow flex items-center justify-center text-gray-400 text-xs',
          buttonChecked: 'h-6 w-6 rounded-sm  bg-white shadow flex items-center justify-center text-purple-400 text-xs',
          checkedPlaceholder: 'rounded-sm w-6 h-6 flex items-center justify-center text-gray-400 text-xs',
          uncheckedPlaceholder: 'rounded-sm w-6 h-6 flex items-center justify-center text-gray-400 text-xs',
        },
        single: {
          wrapper: `bg-gray-100 rounded-full border-2 border-purple-400 p-0.5 border-transparent 
                    focus:ring-2 focus:ring-purple-400 focus:outline-none focus:ring-opacity-50`,
          wrapperChecked: 'bg-purple-400 rounded-full focus:outline-none',
          wrapperDisabled: `bg-gray-100 rounded-full border-2 border-transparent 
                    focus:ring-2 focus:ring-purple-400 focus:outline-none focus:ring-opacity-50`,
          wrapperCheckedDisabled: 'bg-purple-400 focus:outline-none',
          button: 'h-5 w-5 rounded-full bg-white shadow flex items-center justify-center text-gray-400 text-xs',
          buttonChecked:
            'h-5 w-5 rounded-full bg-white shadow flex items-center justify-center text-purple-400 text-xs',
          checkedPlaceholder: 'rounded-full w-5 h-5 flex items-center justify-center text-gray-400 text-xs',
          uncheckedPlaceholder: 'rounded-full w-5 h-5 flex items-center justify-center text-gray-400 text-xs',
        },
      },
    },
  },
  //
  't-datepicker': {
    component: TDatepicker,
    props: {
      fixedClasses: {
        navigator: 'flex',
        navigatorViewButton: 'flex items-center',
        navigatorViewButtonIcon: 'flex-shrink-0 h-5 w-5',
        navigatorViewButtonBackIcon: 'flex-shrink-0 h-5 w-5',
        navigatorLabel: 'flex items-center py-1',
        navigatorPrevButtonIcon: 'h-6 w-6 inline-flex',
        navigatorNextButtonIcon: 'h-6 w-6 inline-flex',
        inputWrapper: 'relative',
        viewGroup: 'inline-flex flex-wrap',
        view: 'w-64',
        calendarDaysWrapper: 'grid grid-cols-7',
        calendarHeaderWrapper: 'grid grid-cols-7',
        monthWrapper: 'grid grid-cols-4',
        yearWrapper: 'grid grid-cols-4',
        input: `block w-full  transition duration-100 ease-in-out border border-solid 
                border-purple-200 focus:border-purple-400 focus:outline-none focus:ring-opacity-50 
                disabled:opacity-50 disabled:cursor-not-allowed`,
        clearButton: 'flex flex-shrink-0 items-center justify-center absolute top-0 m-2 h-6 w-6 mr-1 -right-1',
        clearButtonIcon: 'fill-current h-3 w-3',
      },
      classes: {
        wrapper: 'flex flex-col',
        dropdownWrapper: 'relative z-10',
        dropdown: 'origin-top-left absolute border border-purple-400 rounded shadow bg-white overflow-hidden',
        enterClass: 'opacity-0 scale-95',
        enterActiveClass: 'transition transform ease-out duration-100',
        enterToClass: 'opacity-100 scale-100',
        leaveClass: 'opacity-100 scale-100',
        leaveActiveClass: 'transition transform ease-in duration-75',
        leaveToClass: 'opacity-0 scale-95',
        inlineWrapper: '',
        inlineViews: 'rounded bg-white border mt-1 inline-flex',
        inputWrapper: '',
        input: 'px-1 py-2 text-sm text-black placeholder-gray-400 border-purple-200',
        clearButton: 'hover:bg-gray-100 rounded transition duration-100 ease-in-out text-gray-600',
        clearButtonIcon: '',
        viewGroup: '',
        view: '',
        navigator: 'pt-2 px-3',
        navigatorViewButton: `transition ease-in-out duration-100 inline-flex cursor-pointer 
                              rounded-full px-2 py-1 -ml-1 hover:bg-gray-100`,
        navigatorViewButtonIcon: 'fill-current text-gray-400',
        navigatorViewButtonBackIcon: 'fill-current text-gray-400',
        navigatorViewButtonMonth: 'text-gray-700 font-semibold',
        navigatorViewButtonYear: 'text-gray-500 ml-1',
        navigatorViewButtonYearRange: 'text-gray-500 ml-1',
        navigatorLabel: 'py-1',
        navigatorLabelMonth: 'text-gray-700 font-semibold',
        navigatorLabelYear: 'text-gray-500 ml-1',
        navigatorPrevButton: `transition ease-in-out duration-100 inline-flex cursor-pointer hover:bg-gray-100 
                              rounded-full p-1 ml-2 ml-auto disabled:opacity-50 disabled:cursor-not-allowed`,
        navigatorNextButton: `transition ease-in-out duration-100 inline-flex cursor-pointer hover:bg-gray-100 
                              rounded-full p-1 -mr-1 disabled:opacity-50 disabled:cursor-not-allowed`,
        navigatorPrevButtonIcon: 'text-gray-400',
        navigatorNextButtonIcon: 'text-gray-400',
        calendarWrapper: 'px-3 pt-2',
        calendarHeaderWrapper: '',
        calendarHeaderWeekDay: 'uppercase text-xs text-gray-500 w-8 h-8 flex items-center justify-center',
        calendarDaysWrapper: '',
        calendarDaysDayWrapper: 'w-full h-8 flex flex-shrink-0 items-center',
        otherMonthDay: `text-sm rounded-full w-8 h-8 mx-auto hover:bg-purple-100 text-gray-400 
                        disabled:opacity-50 disabled:cursor-not-allowed`,
        emptyDay: '',
        inRangeFirstDay: 'text-sm bg-purple-400 text-white w-full h-8 rounded-l-full',
        inRangeLastDay: 'text-sm bg-purple-400 text-white w-full h-8 rounded-r-full',
        inRangeDay: 'text-sm bg-purple-200 w-full h-8 disabled:opacity-50 disabled:cursor-not-allowed',
        selectedDay: `text-sm rounded-full w-8 h-8 mx-auto bg-purple-400 text-white 
                      disabled:opacity-50 disabled:cursor-not-allowed`,
        activeDay: 'text-sm rounded-full bg-purple-100 w-8 h-8 mx-auto disabled:opacity-50 disabled:cursor-not-allowed',
        highlightedDay:
          'text-sm rounded-full bg-purple-200 w-8 h-8 mx-auto disabled:opacity-50 disabled:cursor-not-allowed',
        day: 'text-sm rounded-full w-8 h-8 mx-auto hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed',
        today: `text-sm rounded-full w-8 h-8 mx-auto border border-solid border-purple-100 bg-purple-100
                hover:border-purple-200 hover:bg-purple-200 
                disabled:opacity-50 disabled:cursor-not-allowed `,
        monthWrapper: 'px-3 pt-2',
        selectedMonth: 'text-sm rounded w-full h-12 mx-auto bg-purple-400 text-white',
        activeMonth: 'text-sm rounded w-full h-12 mx-auto bg-purple-100',
        month: 'text-sm rounded w-full h-12 mx-auto hover:bg-purple-100',
        yearWrapper: 'px-3 pt-2',
        year: 'text-sm rounded w-full h-12 mx-auto hover:bg-purple-100',
        selectedYear: 'text-sm rounded w-full h-12 mx-auto bg-purple-500 text-white',
        activeYear: 'text-sm rounded w-full h-12 mx-auto bg-purple-100',
      },
      variants: {
        danger: {
          input: 'border-red-500 bg-red-50 placeholder-red-500 text-red-700',
          clearButton: 'hover:bg-red-200 text-red-500',
        },
        datePickerDefault: {
          input: 'px-2 py-2 rounded',
        },
        timepicker: {
          timepickerWrapper: 'flex items-center px-4 py-2 mt-0',
          timepickerTimeWrapper: 'flex items-center space-x-2',
          timepickerTimeFieldsWrapper: `bg-white rounded-md w-full p-0.5 text-right flex items-center 
                                        border border-purple-200 focus:outline-none`,
          timepickerOkButton: `text-purple-400 text-sm uppercase font-semibold transition duration-100 
                                ease-in-out border border-transparent focus:border-purple-400 focus:outline-none 
                                rounded cursor-pointer`,
          timepickerInput: `text-center w-8 border-transparent bg-transparent p-1 h-8 text-sm transition duration-100 
                            ease-in-out border border-purple-200 focus:border-purple-400 focus:ring-1 
                            focus:ring-purple-400 focus:outline-none focus:ring-opacity-50 rounded`,
          timepickerTimeLabel: 'flex-grow text-sm text-gray-500 hidden',
          timepickerAmPmWrapper: `relative inline-flex p-0.5 flex-shrink-0 transition duration-200 ease-in-out 
                          bg-white border border-purple-200 rounded cursor-pointer focus:outline-none`,
          timepickerAmPmWrapperChecked: `relative inline-flex p-0.5 flex-shrink-0 transition duration-200 
                          ease-in-out bg-white border border-purple-200 rounded cursor-pointer focus:outline-none`,
          timepickerAmPmWrapperDisabled:
            'relative inline-flex flex-shrink-0 transition duration-200 ease-in-out opacity-50 cursor-not-allowed',
          timepickerAmPmWrapperCheckedDisabled:
            'relative inline-flex flex-shrink-0 transition duration-200 ease-in-out opacity-50 cursor-not-allowed',
          timepickerAmPmButton: `absolute flex items-center justify-center w-8 h-8 bg-purple-400 text-xs text-white 
                                  transition duration-200 ease-in-out transform translate-x-0 rounded shadow`,
          timepickerAmPmButtonChecked: `absolute flex items-center justify-center w-8 h-8 bg-purple-400 text-xs 
                                        text-white transition duration-200 ease-in-out transform translate-x-full 
                                        bg-white rounded shadow`,
          timepickerAmPmCheckedPlaceholder: 'flex items-center justify-center w-8 h-8 text-xs text-gray-500 rounded-sm',
          timepickerAmPmUncheckedPlaceholder:
            'flex items-center justify-center w-8 h-8 text-xs text-gray-500 rounded-sm',
        },
        clearButton: 'mr-0',
      },
    },
  },
  // ...Rest of the components
};
